import React, { useMemo, useState } from 'react';
import { Box, Button, Form, Input, Text, View } from '../ui';
import { FormField, FormRow, FormSection } from '../ui';
import type { ScenarioTimeline_ShipFragment } from '../../graphql';
import { CargoType, HoldType } from '../../graphql';
import { PortCalls } from './PortCallsDocuments';
import type { PortCallData } from '../core/Core';
import type { CargoAllocation } from '@seaber/common/core/entries';
import { CoreEventType, isTCOrderEventTimeData } from '../core/TimeData';
import { assertDefined } from '../../lib/util';
import type { ScenarioSchedule } from '@seaber/common/core/scenario';
import type { ClientId } from '../core/ClientId';
import { generateClientId } from '../core/ClientId';
import type { EventTimeline } from '../core/Timelines';
import { useMedia } from 'react-media';
import { useOverridableState } from '../hooks/useOverridableState';
import { getCarriedOrdersByPortCall } from './shipment';
import { formatNumber } from '../../lib/format';
import {
  getCompartments,
  isCorrectCargoAllocationType,
} from '../core/Core.cargo-allocation';
import { stringToNumOpt } from '../core/util';
import { compact, sum, uniq } from 'lodash';
import { hasOrganizationConfig } from '../../state/zustand';
import { mapGroupBy, mapKeyBy2 } from '@seaber/common/lib/collections';

enum OperationType {
  Load = 'L',
  Discharge = 'D',
}
interface TanksProps {
  portCalls: PortCallData[];
  scenario: ScenarioSchedule;
  vessel: ScenarioTimeline_ShipFragment | undefined;
  saveAllocations: (allocations: CargoAllocation[]) => void;
  eventTimeline: EventTimeline;
}

const holdTypeToLabel = {
  [HoldType.DECK]: 'Deck',
  [HoldType.HOLD]: 'Hold',
  [HoldType.LIQUID]: 'Liq',
  [HoldType.GAS]: 'Gas',
};

const cargoTypeToLabel = {
  [CargoType.SOLID]: 'Solid',
  [CargoType.LIQUID]: 'Liq',
  [CargoType.GAS]: 'Gas',
};

const Tanks = ({
  vessel,
  scenario,
  portCalls,
  eventTimeline,
  saveAllocations,
}: TanksProps): JSX.Element | null => {
  const { isReadOnly } = scenario;
  const cargoAllocations = useMemo(
    () => scenario.cargoAllocations?.slice() ?? [],
    [scenario.cargoAllocations],
  );

  const [allAllocations, setAllAllocations] = useOverridableState(
    cargoAllocations,
    cargoAllocations,
  );

  const allocationsByOrderIdByCompartmentId = useMemo(
    () =>
      mapKeyBy2(
        allAllocations,
        (a) => a.orderId,
        (a) => a.compartmentId,
      ),
    [allAllocations],
  );

  const [saveSuccess, setSaveSuccess] = useState<boolean>(false);

  const allPortCalls = eventTimeline.voyageGroups[0].portCalls;
  const { scheduledOrderByOrderId, carriedOrdersByPortCall } = useMemo(() => {
    return getCarriedOrdersByPortCall(allPortCalls);
  }, [allPortCalls]);

  const lastAssortmentByPortCallIdByCompartmentId = useMemo(() => {
    const lastAssortmentByPortCallIdByCompartmentId = new Map<
      ClientId | null,
      Map<number, string | undefined>
    >();
    const lastAssortmentByCompartmentId = new Map<number, string | undefined>();

    for (const pc of allPortCalls) {
      pc.tds?.forEach((td) => {
        if (td.event.type === CoreEventType.Discharge) {
          const order = td.event.order;
          allocationsByOrderIdByCompartmentId
            .get(order.id)
            .forEach((allocation) => {
              if (+allocation.amount === 0) return;
              lastAssortmentByCompartmentId.set(
                allocation.compartmentId,
                order.assortmentByAssortmentId?.name,
              );
            });
        }
      });
      lastAssortmentByPortCallIdByCompartmentId.set(
        pc.id,
        new Map(lastAssortmentByCompartmentId),
      );
    }
    return lastAssortmentByPortCallIdByCompartmentId;
  }, [allPortCalls, allocationsByOrderIdByCompartmentId]);

  const [selectedPortCallIndex, setSelectedPortCallIndex] = useState<number>(0);

  const portCall = portCalls.at(selectedPortCallIndex);

  const ordersLoadedInThisPortCall = useMemo(() => {
    const tcTds = portCall?.tds.filter(isTCOrderEventTimeData);
    return (
      tcTds
        ?.filter((td) => td.event.type === CoreEventType.Load)
        .map((td) => td.event.order) ?? []
    );
  }, [portCall]);

  const ordersInThisPortCall = useMemo(() => {
    const tcTds = portCall?.tds.filter(isTCOrderEventTimeData);
    return tcTds
      ?.filter(
        (td) =>
          td.event.type === CoreEventType.Load ||
          td.event.type === CoreEventType.Discharge,
      )
      .map((td) => td.event.order);
  }, [portCall]);

  const allocationsInThisPortCall = useMemo(
    () =>
      allAllocations.filter((a) =>
        ordersInThisPortCall?.some((o) => o.id === a.orderId),
      ),
    [allAllocations, ordersInThisPortCall],
  );

  const currentAllocationsByCompartmentId = useMemo(
    () => mapGroupBy(allocationsInThisPortCall, (x) => x.compartmentId),
    [allocationsInThisPortCall],
  );

  const loadingOrDischargeTypes = useMemo(() => {
    const ldMap = new Map<number, string>();
    const tcTds = portCall?.tds.filter(isTCOrderEventTimeData);
    const filteredTds = tcTds?.filter(
      (td) =>
        td.event.type === CoreEventType.Load ||
        td.event.type === CoreEventType.Discharge,
    );
    for (const td of filteredTds ?? []) {
      ldMap.set(
        td.event.order.id,
        CoreEventType[td.event.type].charAt(0).toUpperCase(),
      );
    }
    return ldMap;
  }, [portCall]);

  const compartments = vessel ? getCompartments(vessel) : [];

  const carried = Array.from(
    carriedOrdersByPortCall.get(portCall?.id ?? null)?.values() ?? [],
  );
  const activeOrders = [...carried, ...ordersLoadedInThisPortCall];

  const totalByCompartmentId = new Map<number, number>();

  let capacityLeftTotal = 0;
  let tankVolumeTotal = 0;

  for (const compartment of compartments) {
    const cid = compartment.id;
    const activeAllocations = activeOrders.flatMap(
      (order) =>
        allocationsByOrderIdByCompartmentId.get(order.id)?.get(cid) ?? [],
    );

    const total = sum(activeAllocations.map((x) => +x.amount) ?? []);
    totalByCompartmentId.set(cid, total);

    tankVolumeTotal += Number(compartment.maxVolume);
    capacityLeftTotal +=
      +compartment.maxVolume - (totalByCompartmentId.get(compartment.id) ?? 0);
  }

  const totalByOrderId = new Map<number, number>();
  for (const order of activeOrders) {
    const oid = order.id;
    let total = 0;
    for (const compartment of compartments) {
      const cid = compartment.id;
      const current = allocationsByOrderIdByCompartmentId.get(oid).get(cid);
      total += +(current?.amount ?? 0);
    }
    totalByOrderId.set(oid, total);
  }

  const lastAssortmentByCompartmentId =
    lastAssortmentByPortCallIdByCompartmentId.get(portCall?.id ?? null);

  const isSmallScreen = useMedia({ query: '(max-width: 992px)' });

  type TotalInfo = {
    amount: number;
    total: number;
    difference: number;
  };

  const orderTotals: TotalInfo = ordersLoadedInThisPortCall.reduce(
    (acc, order) => {
      const volume = order.cargoRanges.volume.max;
      const totalByOrder = totalByOrderId.get(order.id) ?? 0;
      const diff = volume - (totalByOrderId.get(order.id) ?? 0);
      return {
        amount: acc.amount + volume,
        total: acc.total + totalByOrder,
        difference: acc.difference + diff,
      };
    },
    { amount: 0, total: 0, difference: 0 },
  );

  const combineSameAssortment = hasOrganizationConfig(
    'configCargoAllocationCombineAssortment',
  );

  return (
    <View flex={1}>
      <View flex={1} flexDirection={isSmallScreen ? 'column' : 'row'}>
        <PortCalls
          portCalls={portCalls}
          onSelect={setSelectedPortCallIndex}
          selectedPortCall={selectedPortCallIndex}
        />
        <View p={4} overflow="auto">
          <Form width="fit-content">
            <FormSection>
              <FormRow flexWrap="nowrap">
                <FormField width={100}>
                  <Text fontWeight="bold">Tanks/holds</Text>
                </FormField>
                <FormField width={70}>
                  <Text fontWeight="bold">Order amount</Text>
                </FormField>
                <FormField width={70}>
                  <Text fontWeight="bold">Operation type</Text>
                </FormField>
                {compartments?.map((compartment) => {
                  return (
                    <FormField key={compartment.id} width={70}>
                      <Text fontWeight="bold">
                        {compartment.name} (
                        {compartment.type && holdTypeToLabel[compartment.type]})
                      </Text>
                    </FormField>
                  );
                })}
                <FormField width={70}>
                  <Text fontWeight="bold">Total</Text>
                </FormField>
                <FormField width={70}>
                  <Text fontWeight="bold">Difference</Text>
                </FormField>
              </FormRow>
              <FormRow>
                <FormField width={100}>
                  <Text>Previous</Text>
                </FormField>
                <FormField width={70}>
                  <Text />
                </FormField>
                <FormField width={70}>
                  <Text />
                </FormField>
                {compartments?.map((compartment) => {
                  return (
                    <FormField
                      key={compartment.id}
                      width={70}
                      overflowX="hidden"
                    >
                      <Text>
                        {lastAssortmentByCompartmentId?.get(compartment.id) ??
                          ''}
                      </Text>
                    </FormField>
                  );
                })}
              </FormRow>
              {carried.map((order) => {
                const orderAllocationsByCompartmentId =
                  allocationsByOrderIdByCompartmentId.get(order.id);
                const volume = order.cargoRanges.volume.max;
                return (
                  <FormRow key={order.id}>
                    <FormField width={100}>
                      <Text>{order.assortmentByAssortmentId?.name}</Text>
                    </FormField>
                    <FormField width={70}>
                      <Text>{formatNumber(volume)}</Text>
                    </FormField>
                    <FormField width={70}>
                      <Text>O</Text>
                    </FormField>
                    {compartments?.map((compartment) => {
                      return (
                        <FormField key={compartment.id} width={70}>
                          {formatNumber(
                            stringToNumOpt(
                              orderAllocationsByCompartmentId.get(
                                compartment.id,
                              )?.amount,
                            ) ?? null,
                          )}
                        </FormField>
                      );
                    })}
                    <FormField width={70}>
                      {formatNumber(totalByOrderId.get(order.id) ?? null)}
                    </FormField>
                    <FormField width={70}>
                      {formatNumber(
                        volume - (totalByOrderId.get(order.id) ?? 0),
                      )}
                    </FormField>
                  </FormRow>
                );
              })}
              {ordersInThisPortCall?.map((order) => {
                const orderId = order.id;
                const allocationsByCompartmentId =
                  allocationsByOrderIdByCompartmentId.get(orderId);
                const volume = order.cargoRanges.volume.max;
                const totalByOrder = totalByOrderId.get(order.id) ?? 0;
                const diff = volume - (totalByOrderId.get(order.id) ?? 0);
                const cargoType = order.assortmentByAssortmentId?.cargoType;
                const isCargoTypeMismatch =
                  !!cargoType &&
                  !compartments.some((c) =>
                    isCorrectCargoAllocationType(cargoType, c),
                  );
                const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
                  window.HTMLInputElement.prototype,
                  'value',
                )?.set;
                const isVolumeMissing = !volume && !!order.cargoRanges.mass.min;

                const problems: {
                  text: string;
                  color?: string;
                  title?: string;
                }[] = compact([
                  !cargoType && {
                    text: 'Type missing',
                    title:
                      'Cargo type missing, set this for the commodity in admin.',
                  },
                  isVolumeMissing && {
                    text: 'Volume missing',
                    title:
                      'Specific gravity not defined, cannot calculate volume from mass.',
                  },
                  isCargoTypeMismatch && {
                    text: cargoTypeToLabel[cargoType],
                    title: `Cargo type mismatch, no compartment can hold the cargo type ${cargoType}`,
                    color: 'orange',
                  },
                ]);

                return (
                  <FormRow key={orderId} alignItems="center">
                    <FormField width={100}>
                      <Text
                        style={{
                          color:
                            problems.length > 0
                              ? (problems[0].color ?? 'red')
                              : undefined,
                        }}
                      >
                        <span title={problems.map((e) => e.title).join(' ')}>
                          {order.assortmentByAssortmentId?.name}
                          {problems.length > 0
                            ? ` (${problems.map((e) => e.text).join(', ')})`
                            : undefined}
                        </span>
                      </Text>
                    </FormField>
                    <FormField width={70}>
                      <Text color={isVolumeMissing ? 'red' : undefined}>
                        {formatNumber(volume)}
                      </Text>
                    </FormField>
                    <FormField width={70}>
                      <Text>{loadingOrDischargeTypes.get(orderId)}</Text>
                    </FormField>
                    {compartments?.map((compartment) => {
                      const compartmentId = compartment.id;
                      const allocation =
                        allocationsByCompartmentId.get(compartmentId);
                      const isWrongCargoTypeAllocated =
                        !!cargoType &&
                        !isCorrectCargoAllocationType(cargoType, compartment);

                      const allCompartmentAllocations =
                        currentAllocationsByCompartmentId.get(compartment.id) ??
                        [];
                      const allOrdersInCompartment = allCompartmentAllocations
                        .filter((a) => Number(a.amount) > 0)
                        .map((a) => a.orderId);
                      const hasMultipleOrdersInCompartment =
                        uniq(allOrdersInCompartment).length > 1;
                      const hasMultipleAssortmentsInCompartment =
                        uniq(
                          allOrdersInCompartment.map(
                            (orderId) =>
                              activeOrders.find((o) => o.id === orderId)
                                ?.assortmentId,
                          ),
                        ).length > 1;
                      const hasMixedOrdersInCompartment = combineSameAssortment
                        ? hasMultipleAssortmentsInCompartment
                        : hasMultipleOrdersInCompartment;
                      return (
                        <FormField key={compartmentId} width={70}>
                          {isReadOnly ||
                          loadingOrDischargeTypes.get(orderId) ===
                            OperationType.Discharge ? (
                            allocation?.amount &&
                            `-${formatNumber(
                              stringToNumOpt(allocation?.amount) ?? null,
                            )}`
                          ) : (
                            <Input
                              type="number"
                              disabled={isReadOnly}
                              defaultValue={allocation?.amount}
                              style={{
                                color:
                                  isWrongCargoTypeAllocated || !cargoType
                                    ? 'red'
                                    : hasMixedOrdersInCompartment
                                      ? 'orange'
                                      : undefined,
                              }}
                              title={
                                isWrongCargoTypeAllocated
                                  ? 'Wrong cargo type to this hold'
                                  : !cargoType
                                    ? 'Cargo type not defined for the assortment'
                                    : hasMixedOrdersInCompartment
                                      ? combineSameAssortment
                                        ? 'Multiple different assortments in this hold'
                                        : 'Multiple different orders in this hold'
                                      : ''
                              }
                              onFocus={(e) => {
                                const value = +e.target.value;
                                if (value || !nativeInputValueSetter) return;
                                const volumeLeft =
                                  +compartment.maxVolume -
                                  (totalByCompartmentId.get(compartmentId) ??
                                    0);

                                const m = Math.min(
                                  diff,
                                  Number(compartment.maxVolume),
                                  volumeLeft,
                                );
                                nativeInputValueSetter.call(e.target, m);
                                const evt = new Event('input', {
                                  bubbles: true,
                                });
                                e.target.dispatchEvent(evt);
                              }}
                              onChange={(e) => {
                                const value = +e.target.value;
                                const scheduledOrder =
                                  scheduledOrderByOrderId.get(orderId);
                                const soCId = scheduledOrder?.clientId;
                                assertDefined(
                                  soCId,
                                  'scheduled order clientId needs to be defined',
                                );
                                const updatedAllocation: CargoAllocation =
                                  allocation
                                    ? { ...allocation, amount: value }
                                    : {
                                        clientId: generateClientId(),
                                        scheduledOrderClientId: soCId,
                                        compartmentId,
                                        amount: value,
                                        orderId,
                                      };

                                const newAllocations = allocation
                                  ? allAllocations.map((a) =>
                                      a.clientId === allocation.clientId
                                        ? updatedAllocation
                                        : a,
                                    )
                                  : allAllocations.concat(updatedAllocation);
                                setAllAllocations(newAllocations);

                                setSaveSuccess(false);
                              }}
                            />
                          )}
                        </FormField>
                      );
                    })}
                    <FormField width={70}>
                      <Text>{formatNumber(totalByOrder)}</Text>
                    </FormField>
                    <FormField width={70}>
                      <Text
                        color={diff < 0 ? 'red' : diff > 0 ? '#ffa500' : ''}
                      >
                        {formatNumber(diff)}
                      </Text>
                    </FormField>
                  </FormRow>
                );
              })}
              <FormRow>
                <FormField width={100}>
                  <Text>Total</Text>
                </FormField>
                <FormField width={70}>
                  <Text>{formatNumber(orderTotals.amount)}</Text>
                </FormField>
                <FormField width={70}>
                  <Text />
                </FormField>
                {compartments?.map((compartment) => {
                  return (
                    <FormField key={compartment.id} width={70}>
                      {formatNumber(
                        totalByCompartmentId.get(compartment.id) ?? 0,
                      )}
                    </FormField>
                  );
                })}
                <FormField width={70}>
                  <Text>{formatNumber(orderTotals.total)}</Text>
                </FormField>
                <FormField width={70}>
                  <Text color={orderTotals.difference < 0 ? 'red' : ''}>
                    {formatNumber(orderTotals.difference)}
                  </Text>
                </FormField>
              </FormRow>
              <FormRow>
                <FormField width={100}>
                  <Text>Capacity left</Text>
                </FormField>
                <FormField width={70}>
                  <Text>-</Text>
                </FormField>
                <FormField width={70}>
                  <Text />
                </FormField>
                {compartments?.map((compartment) => {
                  const capacityLeft =
                    +compartment.maxVolume -
                    (totalByCompartmentId.get(compartment.id) ?? 0);

                  return (
                    <FormField key={compartment.id} width={70}>
                      <Text color={capacityLeft < 0 ? 'red' : ''}>
                        {formatNumber(capacityLeft)}
                      </Text>
                    </FormField>
                  );
                })}
                <FormField width={70}>
                  <Text color={capacityLeftTotal < 0 ? 'red' : ''}>
                    {formatNumber(capacityLeftTotal) || '-'}
                  </Text>
                </FormField>
                <FormField width={70}>
                  <Text>-</Text>
                </FormField>
              </FormRow>
              <FormRow>
                <FormField width={100}>
                  <Text>Tank volume</Text>
                </FormField>
                <FormField width={70}>
                  <Text>-</Text>
                </FormField>
                <FormField width={70}>
                  <Text />
                </FormField>
                {compartments?.map((compartment) => {
                  return (
                    <FormField key={compartment.id} width={70}>
                      {compartment.maxVolume}
                    </FormField>
                  );
                })}
                <FormField width={70}>
                  <Text color={tankVolumeTotal < 0 ? 'red' : ''}>
                    {formatNumber(tankVolumeTotal) || '-'}
                  </Text>
                </FormField>
                <FormField width={70}>
                  <Text>-</Text>
                </FormField>
              </FormRow>
              <FormRow>
                <Box display="flex" flexDirection="row" alignItems="center">
                  <Button
                    mt={4}
                    disabled={cargoAllocations === allAllocations}
                    onPress={() => {
                      const allocations: CargoAllocation[] = [];
                      allocationsByOrderIdByCompartmentId.forEach((byCId) =>
                        byCId.forEach((allocation) => {
                          allocations.push(allocation);
                        }),
                      );
                      saveAllocations(allocations);
                      setSaveSuccess(true);
                    }}
                  >
                    Save to scenario
                  </Button>
                  {saveSuccess && (
                    <Text fontWeight="bold" color="#5fad09">
                      ✓ Saved successfully
                    </Text>
                  )}
                </Box>
              </FormRow>
            </FormSection>
          </Form>
        </View>
      </View>
    </View>
  );
};

export default Tanks;
